<template>
  <div class="charging-settings-tabs">
    <!-- Réglages de recharge : les politiques (les personnes), les profils d'équipement (les bornes et compteurs), les abonnements. -->
    <!-- Les onglets défilent (flèches) quand la place manque ; le bouton d'import vit à côté, jamais coupé avec eux. -->
    <div class="d-flex align-center flex-wrap mb-4 charging-settings-tabs__bar">
      <v-tabs class="charging-settings-tabs__tabs" background-color="transparent" height="40" show-arrows>
        <v-tab :to="{ name: 'organizations.charging-policies' }">{{ $t('onboarding.charging-settings.policies') }}</v-tab>
        <v-tab :to="{ name: 'organizations.equipment-profiles' }">{{ $t('onboarding.charging-settings.profiles') }}</v-tab>
        <v-tooltip bottom>
          <template #activator="{ on, attrs }">
            <div class="d-flex" v-bind="attrs" v-on="on">
              <v-tab disabled>{{ $t('onboarding.charging-settings.subscriptions') }}</v-tab>
            </div>
          </template>
          <span>{{ $t('onboarding.charging-settings.subscriptions-soon') }}</span>
        </v-tooltip>
      </v-tabs>
      <v-btn v-if="canManage" small outlined color="primary" class="ml-auto my-1" @click="importOpen = true">
        <v-icon small left>mdi-database-import-outline</v-icon>{{ $t('onboarding.config-import.open') }}
      </v-btn>
    </div>

    <!-- Mise en route : abonnement → profil → politique ; chaque étape manquante ouvre son assistant. Repliée quand tout y est. -->
    <v-card v-if="canManage && loaded" outlined class="mb-4 setup-checklist" :class="{ 'setup-checklist--complete': complete }">
      <v-card-text class="py-2">
        <div class="setup-checklist__head" role="button" :aria-expanded="String(expanded)" @click="expanded = !expanded">
          <v-icon small :color="complete ? 'green' : 'orange darken-2'">{{ complete ? 'mdi-check-circle-outline' : 'mdi-format-list-checks' }}</v-icon>
          <span class="text-body-2 font-weight-bold">{{ $t('onboarding.setup-checklist.title') }}</span>
          <span class="text-caption text--secondary">{{ $t('onboarding.setup-checklist.progress', { done: doneCount, total: steps.length }) }}</span>
          <v-spacer />
          <v-icon small>{{ expanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
        </div>
        <div v-if="expanded" class="mt-2">
          <div v-for="step in steps" :key="step.key" class="setup-checklist__step">
            <v-icon small :color="step.done ? 'green' : 'grey'">{{ step.done ? 'mdi-checkbox-marked-circle' : 'mdi-checkbox-blank-circle-outline' }}</v-icon>
            <div class="flex-grow-1">
              <div class="text-body-2" :class="{ 'text--secondary': step.done }">{{ $t(`onboarding.setup-checklist.${step.key}.label`) }}</div>
              <div v-if="!step.done" class="text-caption text--secondary">{{ $t(`onboarding.setup-checklist.${step.key}.explained`) }}</div>
            </div>
            <v-btn v-if="!step.done" small outlined color="primary" @click="act(step.key)">{{ $t(`onboarding.setup-checklist.${step.key}.action`) }}</v-btn>
          </div>
        </div>
      </v-card-text>
    </v-card>

    <equipment-profile-editor v-if="canManage && editorOpen" v-model="editorOpen" :organization-id="organizationId" @saved="profileSaved" />
    <configuration-import-dialog v-if="canManage" v-model="importOpen" :organization-id="organizationId" @imported="profileSaved(null)" />
  </div>
</template>

<script>
import ChargingPolicyRepository from '@repository/ChargingPolicyRepository'
import CommissioningPolicyRepository from '@repository/CommissioningPolicyRepository'
import EquipmentProfileRepository from '@repository/EquipmentProfileRepository'
import ConfigurationImportDialog from './ConfigurationImportDialog.vue'
import EquipmentProfileEditor from './EquipmentProfileEditor.vue'
import { equipmentKinds, kindsGivenBy, PROFILE_KINDS } from './Commissioning/policyCatalogue'

/**
 * Les onglets des réglages de recharge, et la mise en route de l'organisation : un abonnement qui convient, un profil
 * d'équipement, une politique qui en désigne un. Une étape manquante a son bouton, qui ouvre le bon assistant (le
 * panneau du profil, qui sait aussi créer l'abonnement ; la politique). « Importer une configuration » reprend celle
 * d'une autre organisation. Les pages appellent `refresh()` après un changement ; un profil créé d'ici (ou un import,
 * sans identifiant) est signalé par `profile-saved`.
 */
export default {
  name: 'ChargingSettingsTabs',

  components: { ConfigurationImportDialog, EquipmentProfileEditor },

  data() {
    return {
      loaded: false,
      expanded: false,
      hasProfile: false,
      hasPolicy: false,
      defaultPolicyId: null,
      editorOpen: false,
      importOpen: false,
    }
  },

  computed: {
    canManage() {
      return (this.currentUser?.permissions || []).includes('ONBOARDING_MODULE_MANAGE')
    },

    organizationId() {
      return this.$route.params.id
    },

    steps() {
      // L'abonnement n'est pas une étape à part : son onglet n'est pas encore ouvert, il se crée avec le premier profil.
      return [
        { key: 'profile', done: this.hasProfile },
        { key: 'policy', done: this.hasPolicy },
      ]
    },

    doneCount() {
      return this.steps.filter(step => step.done).length
    },

    complete() {
      return this.doneCount === this.steps.length
    },
  },

  created() {
    this.refresh(true)
  },

  methods: {
    /** Relit l'état de la mise en route ; la première fois, l'ouvre s'il reste quelque chose à faire. */
    async refresh(first = false) {
      if (!this.canManage) return
      const [profiles, policies, kinds] = await Promise.all([
        EquipmentProfileRepository.list(this.organizationId).catch(() => []),
        ChargingPolicyRepository.list(this.organizationId).catch(() => []),
        equipmentKinds().catch(() => ({ all: [] })),
      ])
      const equipment = await Promise.all(policies.map(p => CommissioningPolicyRepository.get(this.organizationId, null, p.id).catch(() => null)))
      this.hasProfile = profiles.some(p => !p.archived)
      // Une politique qui donne au moins un équipement à profil (un badge seul ne dit rien de la chaîne).
      this.hasPolicy = equipment.some(policy => kindsGivenBy(policy?.settings, kinds.all).some(kind => PROFILE_KINDS.includes(kind)))
      this.defaultPolicyId = (policies.find(p => p.default) || policies[0] || {}).id || null
      if (first) this.expanded = !this.complete
      this.loaded = true
    },

    act(step) {
      if (step === 'policy') {
        const to = this.defaultPolicyId
          ? { name: 'organizations.charging-policy', params: { id: this.organizationId, policyId: this.defaultPolicyId } }
          : { name: 'organizations.charging-policies', params: { id: this.organizationId } }
        // Déjà sur la page : rien à faire, la politique est sous les yeux.
        this.$router.push(to).catch(() => {})
        return
      }
      // Le profil, et son abonnement sur place s'il n'y en a pas.
      this.editorOpen = true
    },

    async profileSaved(id) {
      this.$emit('profile-saved', id)
      await this.refresh()
    },
  },
}
</script>

<style scoped>
.charging-settings-tabs__tabs {
  flex: 1 1 320px;
  min-width: 0;
}

.setup-checklist__head {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}
.setup-checklist__step {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 0;
}
.setup-checklist--complete {
  opacity: 0.85;
}
</style>
